import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  applyVerticalDemoTemplate,
  expireTenantPackage,
  getTenantVisibilityPreview,
  grantTenantPackage,
  listTenantPackageEntitlements,
  listVerticalDemoTemplates,
  listVerticalPackages,
  reactivateTenantPackage,
  suspendTenantPackage,
  type TenantPackageEntitlement,
  type TenantVisibilityPreview,
  type VerticalDemoTemplate,
  type VerticalPackage,
  type VerticalTemplateApplyResult,
} from '../services/apiService.ts';

interface TenantVerticalPackagingPanelProps {
  tenantId: string;
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return 'not set';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('ro-RO', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const packageModuleCount = (entry: { modules?: Array<unknown> }) =>
  `${entry.modules?.length ?? 0} module${(entry.modules?.length ?? 0) === 1 ? '' : 's'}`;

const TenantVerticalPackagingPanel: React.FC<TenantVerticalPackagingPanelProps> = ({ tenantId }) => {
  const [packages, setPackages] = useState<VerticalPackage[]>([]);
  const [entitlements, setEntitlements] = useState<TenantPackageEntitlement[]>([]);
  const [preview, setPreview] = useState<TenantVisibilityPreview | null>(null);
  const [templates, setTemplates] = useState<VerticalDemoTemplate[]>([]);
  const [grantPackageCode, setGrantPackageCode] = useState('');
  const [templateCode, setTemplateCode] = useState('');
  const [previewUserId, setPreviewUserId] = useState('');
  const [templateResult, setTemplateResult] = useState<VerticalTemplateApplyResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visibleModules = preview?.visibilityContext?.visibleModules ?? [];
  const availableScopes = preview?.visibilityContext?.availableScopes ?? [];
  const warnings = preview?.visibilityContext?.warnings ?? [];

  const activePackageCodes = useMemo(
    () => new Set(entitlements.filter((entry) => entry.active).map((entry) => entry.packageCode)),
    [entitlements]
  );

  const grantablePackages = useMemo(
    () => packages.filter((entry) => entry.active && !activePackageCodes.has(entry.packageCode)),
    [activePackageCodes, packages]
  );

  const loadPanel = useCallback(async () => {
    if (!tenantId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [packageRows, entitlementRows, previewResult, templateRows] = await Promise.all([
        listVerticalPackages(),
        listTenantPackageEntitlements(tenantId),
        getTenantVisibilityPreview(tenantId),
        listVerticalDemoTemplates(),
      ]);
      setPackages(packageRows);
      setEntitlements(entitlementRows);
      setPreview(previewResult);
      setTemplates(templateRows);
      setGrantPackageCode((current) => current || packageRows.find((entry) => entry.active)?.packageCode || '');
      setTemplateCode((current) => current || templateRows[0]?.code || '');
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Vertical packaging data could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void loadPanel();
  }, [loadPanel]);

  const runAction = async (action: () => Promise<unknown>, refresh = true) => {
    setActionLoading(true);
    setError(null);
    try {
      await action();
      if (refresh) {
        await loadPanel();
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Package action failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleGrant = async () => {
    const packageCode = grantPackageCode || grantablePackages[0]?.packageCode || packages[0]?.packageCode;
    if (!packageCode) {
      return;
    }
    await runAction(() => grantTenantPackage(tenantId, {
      packageCode,
      status: 'ACTIVE',
      source: 'SUPERADMIN',
    }));
  };

  const handleDryRunTemplate = async () => {
    if (!templateCode) {
      return;
    }
    const primaryUserId = previewUserId.trim().length > 0 ? Number(previewUserId) : undefined;
    await runAction(async () => {
      const result = await applyVerticalDemoTemplate(tenantId, templateCode, {
        primaryUserId: Number.isFinite(primaryUserId) ? primaryUserId : undefined,
        dryRun: true,
      });
      setTemplateResult(result);
    }, false);
  };

  const handleApplyTemplate = async () => {
    if (!templateCode) {
      return;
    }
    const primaryUserId = previewUserId.trim().length > 0 ? Number(previewUserId) : undefined;
    await runAction(async () => {
      const result = await applyVerticalDemoTemplate(tenantId, templateCode, {
        primaryUserId: Number.isFinite(primaryUserId) ? primaryUserId : undefined,
        dryRun: false,
      });
      setTemplateResult(result);
    });
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="overline" color="text.secondary">Tenant scope control</Typography>
        <Typography variant="h5">Vertical Packaging</Typography>
        <Typography variant="body2" color="text.secondary">
          Superadmin package grants, entitlement lifecycle, and visibility preview for this tenant.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline" color="text.secondary">Entitlements</Typography>
          <Typography variant="h6">{entitlements.length}</Typography>
          <Typography variant="body2" color="text.secondary">
            {entitlements.filter((entry) => entry.active).length} active package grants
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline" color="text.secondary">Visible modules</Typography>
          <Typography variant="h6">{visibleModules.length}</Typography>
          <Typography variant="body2" color="text.secondary">
            {availableScopes.length} resolved scope{availableScopes.length === 1 ? '' : 's'}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline" color="text.secondary">Warnings</Typography>
          <Typography variant="h6">{warnings.length}</Typography>
          <Typography variant="body2" color="text.secondary">
            Visibility diagnostics from the backend resolver
          </Typography>
        </Paper>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Current entitlements</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Package</TableCell>
              <TableCell>Vertical</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Modules</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Starts</TableCell>
              <TableCell>Ends</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entitlements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">No package entitlements configured.</Typography>
                </TableCell>
              </TableRow>
            ) : entitlements.map((entry) => (
              <TableRow key={entry.entitlementId}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{entry.packageName || entry.packageCode}</Typography>
                  <Typography variant="caption" color="text.secondary">{entry.packageCode}</Typography>
                </TableCell>
                <TableCell>{entry.verticalCode || 'Shared'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={entry.status}
                    color={entry.active ? 'success' : 'default'}
                    variant={entry.active ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>{packageModuleCount(entry)}</TableCell>
                <TableCell>{entry.source || 'manual'}</TableCell>
                <TableCell>{formatDate(entry.startsAt)}</TableCell>
                <TableCell>{formatDate(entry.endsAt)}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {entry.active ? (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={actionLoading}
                          onClick={() => runAction(() => expireTenantPackage(tenantId, entry.entitlementId))}
                        >
                          Expire {entry.packageCode}
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          disabled={actionLoading}
                          onClick={() => runAction(() => suspendTenantPackage(tenantId, entry.entitlementId))}
                        >
                          Suspend
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={actionLoading}
                        onClick={() => runAction(() => reactivateTenantPackage(tenantId, entry.entitlementId))}
                      >
                        Reactivate
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Available packages</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
          <TextField
            select
            SelectProps={{ native: true }}
            label="Package to grant"
            size="small"
            value={grantPackageCode}
            onChange={(event) => setGrantPackageCode(event.target.value)}
            sx={{ minWidth: 260 }}
          >
            {packages.map((entry) => (
              <option key={entry.packageCode} value={entry.packageCode}>
                {entry.packageCode}
              </option>
            ))}
          </TextField>
          <Button variant="contained" disabled={!grantPackageCode || actionLoading} onClick={handleGrant}>
            Grant Package
          </Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Package</TableCell>
              <TableCell>Vertical</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Modules</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {packages.map((entry) => (
              <TableRow key={entry.packageCode}>
                <TableCell>{entry.packageName || entry.packageCode}</TableCell>
                <TableCell>{entry.verticalCode || 'Shared'}</TableCell>
                <TableCell>{entry.description || 'No description'}</TableCell>
                <TableCell>{packageModuleCount(entry)}</TableCell>
                <TableCell>
                  <Chip size="small" label={entry.active ? 'Active' : 'Inactive'} variant="outlined" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Visibility preview</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Scopes</Typography>
            <Stack spacing={0.75}>
              {availableScopes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No VPF scopes resolved.</Typography>
              ) : availableScopes.map((scope) => (
                <Chip
                  key={scope.scopeKey}
                  label={scope.label || scope.scopeKey}
                  size="small"
                  variant={preview?.visibilityContext?.defaultScope?.scopeKey === scope.scopeKey ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Modules</Typography>
            <Stack spacing={0.75}>
              {visibleModules.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No modules visible for the selected preview.</Typography>
              ) : visibleModules.map((module) => (
                <Typography key={module.code} variant="body2">
                  {module.name || module.code}
                </Typography>
              ))}
            </Stack>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Warnings</Typography>
            <Stack spacing={0.75}>
              {warnings.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No resolver warnings.</Typography>
              ) : warnings.map((warning) => (
                <Alert key={`${warning.code}-${warning.message}`} severity="warning" variant="outlined">
                  {warning.code}: {warning.message}
                </Alert>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Demo template apply</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            select
            SelectProps={{ native: true }}
            label="Demo template"
            size="small"
            value={templateCode}
            onChange={(event) => setTemplateCode(event.target.value)}
            sx={{ minWidth: 260 }}
          >
            {templates.map((entry) => (
              <option key={entry.code} value={entry.code}>
                {entry.label || entry.code}
              </option>
            ))}
          </TextField>
          <TextField
            label="Preview user ID"
            size="small"
            value={previewUserId}
            onChange={(event) => setPreviewUserId(event.target.value)}
            sx={{ width: 180 }}
          />
          <Button variant="outlined" disabled={!templateCode || actionLoading} onClick={handleDryRunTemplate}>
            Dry-run Template
          </Button>
          <Button variant="contained" disabled={!templateCode || actionLoading} onClick={handleApplyTemplate}>
            Apply Template
          </Button>
        </Stack>
        {templateResult && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="subtitle2">{templateResult.dryRun ? 'Planned changes' : 'Applied changes'}</Typography>
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              {((templateResult.dryRun ? templateResult.plannedChanges : templateResult.appliedChanges) || []).map((entry) => (
                <Typography key={entry} variant="body2">{entry}</Typography>
              ))}
              {(templateResult.warnings || []).map((entry) => (
                <Alert key={entry} severity="warning" variant="outlined">{entry}</Alert>
              ))}
            </Stack>
          </Box>
        )}
      </Paper>
    </Stack>
  );
};

export default TenantVerticalPackagingPanel;
